import { useEffect, useRef } from 'react';
import type { WorkOrder } from '@/types/maintenance';
import { queueAgeDays } from '@/lib/workOrderUtils';

interface EscalationWO {
  id: string;
  wo_id: string;
  mold_id: string | null;
  mold_label: string;
  age_days: number;
}

const CHECK_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes

/**
 * Runs on mount (and every 10 min) to detect work orders that have aged past
 * the configured threshold and sends escalation notifications to managers.
 * Should only be called for manager / admin / super_admin users.
 */
export function useAutoEscalation(
  workOrders: WorkOrder[],
  agingThresholdDays: number,
) {
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const runCheck = async () => {
    const aged: EscalationWO[] = workOrders
      .filter(wo =>
        wo.status !== 'closed' &&
        wo.status !== 'blocked' &&
        queueAgeDays(wo) >= agingThresholdDays,
      )
      .map(wo => ({
        id: wo.id,
        wo_id: wo.wo_id,
        mold_id: wo.mold_id ?? null,
        mold_label: wo.mold?.mold_id ?? wo.mold_id ?? 'Unknown mold',
        age_days: queueAgeDays(wo),
      }));

    if (!aged.length) return;

    try {
      await fetch('/api/notifications/escalation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ work_orders: aged }),
      });
    } catch {
      // Network failure — silent, will retry next interval
    }
  };

  useEffect(() => {
    runCheck();
    timerRef.current = setInterval(runCheck, CHECK_INTERVAL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workOrders, agingThresholdDays]);
}
